import { useMutation } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { LoaderCircle } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";
import { Logo } from "~/components/logo";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import authClient from "~/lib/auth/auth-client";

const searchSchema = z.object({
  token: z.string().optional(),
  error: z.string().optional(),
});

export const Route = createFileRoute("/(auth-pages)/reset-password")({
  component: ResetPasswordForm,
  validateSearch: searchSchema,
});

function ResetPasswordForm() {
  const { token, error } = Route.useSearch();
  const navigate = useNavigate();

  const { mutate: resetPasswordMutate, isPending } = useMutation({
    mutationFn: async (data: { newPassword: string; confirmPassword: string }) => {
      if (!token) throw new Error("Missing reset token");
      await authClient.resetPassword({
        newPassword: data.newPassword,
        token,
      });
    },
    onSuccess: () => {
      toast.success("Password reset successfully. You can now login.");
      navigate({ to: "/login" });
    },
    onError: (err) => {
      toast.error(err.message || "Failed to reset password.");
    },
  });

  const handleSubmit = (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isPending) return;

    const formData = new FormData(e.currentTarget);
    const newPassword = formData.get("new_password") as string;
    const confirmPassword = formData.get("confirm_password") as string;

    if (!newPassword || !confirmPassword) return;

    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }

    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }

    resetPasswordMutate({ newPassword, confirmPassword });
  };

  if (error === "INVALID_TOKEN") {
    return (
      <div className="flex flex-col gap-6">
        <div className="flex flex-col items-center gap-4 py-4">
          <a href="/" className="flex flex-col items-center gap-2 font-medium">
            <Logo size="lg" showText={false} />
            <span className="sr-only">quickprompt.dev</span>
          </a>
          <h1 className="text-2xl font-bold tracking-tight">Invalid reset link</h1>
          <p className="text-muted-foreground text-center text-sm">
            This reset link is invalid or has expired.
          </p>
        </div>
        <div className="text-center text-sm">
          <Link to="/forgot-password" className="underline underline-offset-4">
            Request a new reset link
          </Link>
        </div>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="flex flex-col gap-6">
        <div className="flex flex-col items-center gap-4 py-4">
          <a href="/" className="flex flex-col items-center gap-2 font-medium">
            <Logo size="lg" showText={false} />
            <span className="sr-only">quickprompt.dev</span>
          </a>
          <h1 className="text-2xl font-bold tracking-tight">Missing reset token</h1>
          <p className="text-muted-foreground text-center text-sm">
            Please use the link from your email.
          </p>
        </div>
        <div className="text-center text-sm">
          <Link to="/forgot-password" className="underline underline-offset-4">
            Request a new reset link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <form onSubmit={handleSubmit}>
        <div className="flex flex-col gap-6">
          <div className="flex flex-col items-center gap-4 py-4">
            <a href="/" className="flex flex-col items-center gap-2 font-medium">
              <Logo size="lg" showText={false} />
              <span className="sr-only">quickprompt.dev</span>
            </a>
            <h1 className="text-2xl font-bold tracking-tight">Reset your password</h1>
            <p className="text-muted-foreground text-center text-sm">
              Enter your new password below.
            </p>
          </div>
          <div className="flex flex-col gap-5">
            <div className="grid gap-2">
              <Label htmlFor="new_password">New Password</Label>
              <Input
                id="new_password"
                name="new_password"
                type="password"
                placeholder="New password"
                readOnly={isPending}
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="confirm_password">Confirm Password</Label>
              <Input
                id="confirm_password"
                name="confirm_password"
                type="password"
                placeholder="Confirm new password"
                readOnly={isPending}
                required
              />
            </div>
            <Button type="submit" className="mt-2 w-full" size="lg" disabled={isPending}>
              {isPending && <LoaderCircle className="animate-spin" />}
              {isPending ? "Resetting..." : "Reset password"}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
